import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Table, Button } from '@agconnections/grow-ui';
import { getCorrectUnitAbbreviation } from 'helpers/unitsAbbreviation';
import FormattedNumber from 'components/FormattedNumber';

import ProductsModal from '../../../components/ProductsModal';

const ProductsTableRow = ({
  product,
  applicationType,
  memberRole,
  memberFinancialAccess
}) => {
  const [productsModalOpen, setProductsModalOpen] = useState(false);

  const applicationUnit =
    applicationType === 'ratePerTankValue'
      ? 'ratePerTankUnit'
      : 'ratePerAreaUnit';

  return (
    <>
      <ProductsModal
        open={productsModalOpen}
        onClose={() => {
          setProductsModalOpen(false);
        }}
        product={product}
        applicationType={applicationType}
        memberRole={memberRole}
        memberFinancialAccess={memberFinancialAccess}
      />
      <Table.Row key={product.id}>
        <Table.Cell width="20rem">
          <span className="flex flex-col">
            <span
              className="w-64 pb-2 text-sm font-bold text-left truncate"
              data-testid="product-name"
            >
              {product.productName}
            </span>
            <span
              className="w-64 text-sm text-left text-gray-600 truncate pb-2"
              data-testid="product-manufacturer"
            >
              {product.isCustom && <>Custom</>}
              {product.isCustom && product.manufacturerName && <> • </>}
              {product.manufacturerName}
            </span>
          </span>
        </Table.Cell>
        <Table.Cell>
          <FormattedNumber roundedTo={2}>
            {product?.[applicationType]}
          </FormattedNumber>{' '}
          {getCorrectUnitAbbreviation(product?.[applicationUnit])}
        </Table.Cell>
        <Table.Cell>
          <FormattedNumber roundedTo={2}>
            {product.appliedAreaValue}
          </FormattedNumber>{' '}
          {product.appliedAreaUnit}
        </Table.Cell>
        <Table.Cell>
          <FormattedNumber roundedTo={2}>
            {product.coveragePercent * 100}
          </FormattedNumber>{' '}
          %
        </Table.Cell>
        <Table.Cell>
          <FormattedNumber roundedTo={2}>
            {product.totalProductValue}
          </FormattedNumber>{' '}
          {getCorrectUnitAbbreviation(product?.totalProductUnit)}
        </Table.Cell>
        <Table.Cell width="20rem">
          <div className="mr-6">
            {(memberRole === 'Full control' || memberRole === 'View Only') &&
            memberFinancialAccess === 'none' ? null : (
              <FormattedNumber unit="usCurrency" roundedTo={2}>
                {product.totalCostAtTimeOfCreation}
              </FormattedNumber>
            )}
          </div>
          <div
            data-testid="product-table-view-button"
            className="mr-8 absolute right-0 flex-none w-10"
          >
            <Button
              type="link-default"
              onClick={() => setProductsModalOpen(true)}
              data-html2canvas-ignore
            >
              View
            </Button>
          </div>
        </Table.Cell>
      </Table.Row>
    </>
  );
};

ProductsTableRow.propTypes = {
  applicationType: PropTypes.string.isRequired,
  memberRole: PropTypes.string.isRequired,
  memberFinancialAccess: PropTypes.string.isRequired,
  product: PropTypes.shape({
    applicationMethod: PropTypes.string,
    appliedAreaUnit: PropTypes.string,
    appliedAreaValue: PropTypes.number,
    areaUnit: PropTypes.string,
    associatedProduct: PropTypes.bool,
    averagePriceAtTimeOfCreation: PropTypes.number,
    averagePriceUnitAtTimeOfCreation: PropTypes.string,
    coveragePercent: PropTypes.number,
    isCustom: PropTypes.bool,
    hasCost: PropTypes.bool,
    id: PropTypes.string,
    manufacturerName: PropTypes.string,
    productId: PropTypes.string,
    productName: PropTypes.string,
    ratePerAreaUnit: PropTypes.string,
    ratePerAreaValue: PropTypes.number,
    ratePerTankUnit: PropTypes.string,
    ratePerTankValue: PropTypes.number,
    shareOwnerInformation: PropTypes.shape({
      percentGrowerResponsibility: PropTypes.number,
      totalFromGrowerInventoryQuantity: PropTypes.number,
      totalFromGrowerInventoryUnit: PropTypes.string
    }),
    specificCostPerUnit: PropTypes.number,
    specificCostUnit: PropTypes.string,
    targetPest: PropTypes.shape({
      latinNames: PropTypes.string,
      name: PropTypes.string
    }),
    totalCostAtTimeOfCreation: PropTypes.number,
    totalProductUnit: PropTypes.string,
    totalProductValue: PropTypes.number
  }).isRequired
};

export default ProductsTableRow;
